interface PinnedDragStartParams {
  readonly event: PointerEvent;
  readonly isPinned: boolean;
  readonly isFullscreen: boolean;
  readonly isMobile: boolean;
  readonly hostElement: HTMLElement;
  readonly currentTranslateX: number;
  readonly setTranslateX: (nextValue: number) => void;
  readonly setDragging: (isDragging: boolean) => void;
}

interface DrawerResizeStartParams {
  readonly event: PointerEvent;
  readonly isFullscreen: boolean;
  readonly isMobile: boolean;
  readonly currentDrawerWidth: number;
  readonly minDrawerWidth: number;
  readonly maxDrawerWidth: number;
  readonly setDrawerWidth: (nextValue: number) => void;
  readonly setResizing: (isResizing: boolean) => void;
}

interface ClampPinnedTranslateXParams {
  readonly isPinned: boolean;
  readonly isFullscreen: boolean;
  readonly isMobile: boolean;
  readonly hostElement: HTMLElement;
  readonly currentTranslateX: number;
  readonly setTranslateX: (nextValue: number) => void;
}

interface SyncPinnedLayoutParams {
  readonly isPinned: boolean;
  readonly isDrawerVisible: boolean;
  readonly isFullscreen: boolean;
  readonly isMobile: boolean;
}

/**
 * Helper for pinned drawer drag interactions and layout synchronization.
 */
export class SpotlightAssistantLayoutHelper {
  private pinnedDragDetach?: () => void;
  private pinnedDragStartX = 0;
  private pinnedDragStartTranslateX = 0;
  private pinnedDragDrawerWidth = 0;
  private pinnedDragPointerId: number | null = null;
  private drawerResizeDetach?: () => void;
  private drawerResizePointerId: number | null = null;
  private drawerResizeStartX = 0;
  private drawerResizeStartWidth = 0;

  startPinnedDrag(params: PinnedDragStartParams): void {
    if (!params.isPinned || params.isFullscreen || params.isMobile) {
      return;
    }
    if (params.event.button !== 0) {
      return;
    }
    if (params.event.target instanceof Element) {
      if (params.event.target.closest('.ai-assistant-drawer__header-actions')) {
        return;
      }
    }
    const headerEl = params.event.currentTarget as HTMLElement | null;
    if (!headerEl) {
      return;
    }
    const drawerEl = params.hostElement.querySelector('.ai-assistant-drawer') as HTMLElement | null;
    this.pinnedDragDrawerWidth = drawerEl?.offsetWidth ?? 400;
    this.pinnedDragStartX = params.event.clientX;
    this.pinnedDragStartTranslateX = params.currentTranslateX;
    this.pinnedDragPointerId = params.event.pointerId;
    headerEl.setPointerCapture(params.event.pointerId);
    params.setDragging(true);
    const onMove = (moveEvent: PointerEvent): void => {
      if (this.pinnedDragPointerId !== moveEvent.pointerId) {
        return;
      }
      const deltaX = moveEvent.clientX - this.pinnedDragStartX;
      const viewportWidth = window.innerWidth;
      const maxShift = Math.max(0, viewportWidth - this.pinnedDragDrawerWidth);
      const minTranslateX = -maxShift;
      const nextTranslateX = Math.min(
        0,
        Math.max(minTranslateX, this.pinnedDragStartTranslateX + deltaX)
      );
      params.setTranslateX(nextTranslateX);
    };
    const onEnd = (endEvent: PointerEvent): void => {
      if (this.pinnedDragPointerId !== endEvent.pointerId) {
        return;
      }
      this.pinnedDragPointerId = null;
      params.setDragging(false);
      this.detachPinnedDrag(params.setDragging);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
    this.pinnedDragDetach = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
    };
  }

  startDrawerResize(params: DrawerResizeStartParams): void {
    if (params.isFullscreen || params.isMobile || params.event.button !== 0) {
      return;
    }
    const handleEl = params.event.currentTarget as HTMLElement;
    this.drawerResizeStartX = params.event.clientX;
    this.drawerResizeStartWidth = params.currentDrawerWidth;
    this.drawerResizePointerId = params.event.pointerId;
    handleEl.setPointerCapture(params.event.pointerId);
    params.setResizing(true);
    const onMove = (moveEvent: PointerEvent): void => {
      if (this.drawerResizePointerId !== moveEvent.pointerId) {
        return;
      }
      const deltaX = this.drawerResizeStartX - moveEvent.clientX;
      const nextWidth = Math.min(
        params.maxDrawerWidth,
        Math.max(params.minDrawerWidth, this.drawerResizeStartWidth + deltaX)
      );
      params.setDrawerWidth(nextWidth);
    };
    const onEnd = (endEvent: PointerEvent): void => {
      if (this.drawerResizePointerId !== endEvent.pointerId) {
        return;
      }
      this.drawerResizePointerId = null;
      params.setResizing(false);
      this.detachDrawerResize(params.setResizing);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
    this.drawerResizeDetach = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
    };
  }

  detachPinnedDrag(setDragging: (isDragging: boolean) => void): void {
    if (this.pinnedDragDetach) {
      this.pinnedDragDetach();
      this.pinnedDragDetach = undefined;
    }
    this.pinnedDragPointerId = null;
    setDragging(false);
  }

  detachDrawerResize(setResizing: (isResizing: boolean) => void): void {
    if (this.drawerResizeDetach) {
      this.drawerResizeDetach();
      this.drawerResizeDetach = undefined;
    }
    this.drawerResizePointerId = null;
    setResizing(false);
  }

  clampPinnedTranslateX(params: ClampPinnedTranslateXParams): void {
    if (!params.isPinned || params.isFullscreen || params.isMobile) {
      return;
    }
    const drawerEl = params.hostElement.querySelector('.ai-assistant-drawer') as HTMLElement | null;
    const drawerWidth = drawerEl?.offsetWidth ?? 400;
    const maxShift = Math.max(0, window.innerWidth - drawerWidth);
    const minTranslateX = -maxShift;
    const nextTranslateX = Math.min(0, Math.max(minTranslateX, params.currentTranslateX));
    if (nextTranslateX !== params.currentTranslateX) {
      params.setTranslateX(nextTranslateX);
    }
  }

  syncPinnedLayout(params: SyncPinnedLayoutParams): void {
    const shouldApply =
      params.isPinned && params.isDrawerVisible && !params.isFullscreen && !params.isMobile;
    document.documentElement.classList.toggle('ai-assistant--pinned', shouldApply);
  }
}
